import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';

import { usePatientListStore } from '../store/patient-list.store';
import type {
  Patient,
  PatientSortKey,
} from '../types/patient.types';
import {
  dashIfBlank,
  formatDate,
  formatDischargeDateList,
  formatUid,
  maskName,
  maskPhone,
} from '../utils/format-patient';
import { PatientStatusBadge } from './PatientStatusBadge';

interface PatientListTableProps {
  patients: readonly Patient[];
  onRowClick?: (id: number) => void;
}

interface ColumnDef {
  // PRD §4.1 — Phone and Status are not sortable.
  key: 'phone' | PatientSortKey | 'status';
  i18nKey: string;
  width?: number;
  sortable: boolean;
}

// 8 columns per PRD §2.2 #7 / §4.1. Every column carries an explicit width so the
// table distributes space proportionally instead of letting Name absorb all the
// slack (which left a large empty gap). Status is widened for the longer EN labels.
const COLUMNS: readonly ColumnDef[] = [
  { key: 'id', i18nKey: 'pid', width: 80, sortable: true },
  { key: 'name', i18nKey: 'name', width: 160, sortable: true },
  { key: 'gender', i18nKey: 'gender', width: 90, sortable: true },
  { key: 'birthDate', i18nKey: 'birthDate', width: 120, sortable: true },
  { key: 'phone', i18nKey: 'phone', width: 150, sortable: false },
  { key: 'dischargeDate', i18nKey: 'dischargeDate', width: 130, sortable: true },
  { key: 'status', i18nKey: 'status', width: 170, sortable: false },
  { key: 'registeredAt', i18nKey: 'registeredAt', width: 130, sortable: true },
];

// PRD §3.3 3-click cycle: at rest (userSorted=false), the default-sort column
// (registeredAt) shows its baseline ↓ glyph; all other sortable columns show ↕.
// Once a user picks a column, only that column shows ↑/↓ until the 3rd click
// clears it back to the default state.
function renderSortIcon(
  col: ColumnDef,
  sortKey: PatientSortKey,
  sortDir: 'asc' | 'desc',
  userSorted: boolean,
): string {
  if (!col.sortable) return '';
  if (userSorted) {
    if (sortKey !== col.key) return '↕';
    return sortDir === 'asc' ? '↑' : '↓';
  }
  return col.key === 'registeredAt' ? '↓' : '↕';
}

function ColumnHeaders() {
  const { t } = useTranslation('admin');
  const sort = usePatientListStore((s) => s.sort);
  const userSorted = usePatientListStore((s) => s.userSorted);
  const toggleSort = usePatientListStore((s) => s.toggleSort);

  return (
    <tr>
      {COLUMNS.map((col) => (
        <th
          key={col.key}
          style={{
            width: col.width,
            cursor: col.sortable ? 'pointer' : undefined,
          }}
          onClick={
            col.sortable ? () => toggleSort(col.key as PatientSortKey) : undefined
          }
        >
          {t(`patientManagement.list.columns.${col.i18nKey}`)}{' '}
          {col.sortable && (
            <span className="sort-icon">
              {renderSortIcon(col, sort.key, sort.dir, userSorted)}
            </span>
          )}
        </th>
      ))}
    </tr>
  );
}

// PRD §3.1: rows are clickable; clicking opens the 480px slide panel.
// PRD §4.1: the identifier column renders the UID (`U-{id}`). The memo badge
// was removed with the medical-staff memo feature (v1.7).
//
// SHCT-737 — PRD 02 v1.20 §4.1 puts a 보호자 badge next to the masked name, the same mark the detail
// panel and the medical web's patient card carry (의료진웹 03 §2.3). Admin is the screen that handles
// EVERY patient, so not knowing a record is guardian-proxy means contacting the wrong person.
//
// The badge renders off `appUserType`, which `patientFromWire` already folds to the lowercase union for
// list rows exactly as it does for the detail response. The list DTO does not carry the field yet
// (`PatientListItemResponse`, BE note §3), so today the value is undefined and no badge is drawn — and
// undefined is the right blank here: an absent field is "unknown", never "본인". Nothing further is
// needed on this side once the server sends it.
function PatientListRow({
  patient,
  onClick,
}: {
  patient: Patient;
  onClick?: () => void;
}) {
  const { t } = useTranslation('admin');
  const pendingLabel = t('patientManagement.list.dischargePending');

  return (
    <tr
      style={{ cursor: onClick ? 'pointer' : undefined }}
      onClick={onClick}
    >
      <td>{formatUid(patient.id)}</td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {maskName(patient.name)}
          {patient.appUserType === 'guardian' && (
            <span className="badge badge-info">{t('patientManagement.badges.guardian')}</span>
          )}
        </span>
      </td>
      <td>{patient.gender ? t(`patientManagement.genders.${patient.gender}`) : '-'}</td>
      <td>{formatDate(patient.birthDate)}</td>
      <td>{dashIfBlank(maskPhone(patient.phone))}</td>
      <td>{formatDischargeDateList(patient.dischargeDate, pendingLabel)}</td>
      <td>
        <PatientStatusBadge patient={patient} />
      </td>
      <td>{formatDate(patient.registeredAt)}</td>
    </tr>
  );
}

export function PatientListTable({ patients, onRowClick }: PatientListTableProps) {
  return (
    <div className="card">
      <table className="table table--clickable" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {patients.map((p) => (
            <PatientListRow
              key={p.id}
              patient={p}
              onClick={onRowClick ? () => onRowClick(p.id) : undefined}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// PRD §5: loading skeleton = 5 rows × 8 cols.
export function PatientListSkeleton() {
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {COLUMNS.map((col) => (
                <td key={col.key}>
                  <div
                    className="animate-pulse"
                    style={{ height: 14, background: 'var(--muted)', borderRadius: 4 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// PRD §3.1 / §3.2: empty copy differs when search/status filter is active.
export function PatientListEmpty({ filtered }: { filtered: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={COLUMNS.length}
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'var(--muted-foreground)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Users size={48} color="var(--muted-foreground)" />
                <p style={{ margin: 0 }}>
                  {filtered
                    ? t('patientManagement.list.emptySearch')
                    : t('patientManagement.list.empty')}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
